import { defineStore } from 'pinia'
import { ref, type Ref } from 'vue'
import { useAlertStore } from './alerts'
import api, { getToken, setToken } from '@/services/api'

type UserObj = { id: string; name: string; email: string; role?: string } | null

export const useAuthStore = defineStore('auth', () => {
  // State
  const user: Ref<UserObj> = ref(null)
  const isAuthReady: Ref<boolean> = ref(false)
  const appId: Ref<string> = ref('default-app-id')
  const isLoggingIn: Ref<boolean> = ref(false)
  const error: Ref<string | null> = ref(null)

  async function initialize(): Promise<void> {
    // CRITICAL: Check if server is configured FIRST
    // Import here to avoid circular dependency
    const { isServerConfigured } = await import('@/services/serverConfig')

    if (!isServerConfigured()) {
      // Server not configured yet, skip auth check
      console.log('[Auth] Server not configured, skipping auth initialization')
      user.value = null
      isAuthReady.value = true
      return
    }

    // If we have a stored token, validate it by fetching user synchronously
    const token = getToken()
    if (token) {
      console.log('[Auth] Validating saved login token...')
      try {
        // Add timeout to prevent infinite loading
        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 5000) // 5 second timeout (reduced from 10s)

        const userData = await api.apiFetch('/user', { signal: controller.signal } as any)
        clearTimeout(timeoutId)

        user.value = userData || null
        isAuthReady.value = true
        console.log('[Auth] Login token valid.')
      } catch (err: any) {
        console.warn('[Auth] Token validation failed, removing invalid token:', err)

        // If timeout or network error, clear token
        if (err.name === 'AbortError') {
          console.error('[Auth] Token validation timeout - server may be unreachable')
        }

        setToken(null) // Remove invalid token
        user.value = null
        isAuthReady.value = true
      }
      return
    }

    // If we're using Sanctum session-based auth try to revalidate session (synchronously)
    if (api.USE_SANCTUM) {
      console.log('[Auth] Checking existing SPA session (Sanctum)…')
      try {
        const u = await api.revalidateUser()
        if (u) user.value = u
        isAuthReady.value = true
        console.log('[Auth] Session valid.')
      } catch (err) {
        console.warn('[Auth] Sanctum revalidate failed:', err)
        user.value = null
        isAuthReady.value = true
      }
      return
    }

    // No token -> keep demo behaviour but mark ready so app mounts
    user.value = null
    isAuthReady.value = true
    console.log('[Auth] Auth ready (demo / not signed in).')
  }

  async function login(email: string, password: string) {
    isLoggingIn.value = true
    error.value = null
    const alertStore = useAlertStore()
    try {
      const data = await api.login(email, password)
      // api.login already stores token
      user.value = data.user || null
      isAuthReady.value = true
      alertStore.showAlert('Login berhasil', 'success')
      return data
    } catch (err: any) {
      error.value = err.message || 'Login gagal'
      alertStore.showError(error.value ?? 'Login gagal')
      throw err
    } finally {
      isLoggingIn.value = false
    }
  }

  async function logout() {
    try {
      await api.logout()
    } catch {
      // ignore errors but still remove token
    }
    user.value = null
    isAuthReady.value = true
    setToken(null)
  }

  return { user, isAuthReady, appId, isLoggingIn, error, initialize, login, logout }
})
